import { useState } from 'preact/hooks';
import {
  generateNewIdentity,
  loginWithNsec,
  hasNip07,
} from '../lib/nostr';
import { ApiError, getStoredNsec } from '../lib/api';

interface LoginProps {
  onAuthenticated: () => void;
}

type Mode = 'idle' | 'generating' | 'nsec-paste' | 'nsec-submitting';

export function Login({ onAuthenticated }: LoginProps) {
  const [mode, setMode] = useState<Mode>('idle');
  const [nsecInput, setNsecInput] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [generated, setGenerated] = useState<{
    nsec: string;
    npub: string;
  } | null>(null);
  const [copied, setCopied] = useState(false);

  async function handleGenerate() {
    setError(null);
    setMode('generating');
    try {
      const identity = await generateNewIdentity();
      setGenerated({ nsec: '', npub: identity.npub });
      const nsec = getStoredNsec();
      if (nsec) setGenerated({ nsec, npub: identity.npub });
      onAuthenticated();
    } catch (err) {
      setError(errMessage(err, 'Failed to generate identity'));
      setMode('idle');
    }
  }

  async function handleNsecSubmit(e: Event) {
    e.preventDefault();
    setError(null);
    if (!nsecInput.trim().startsWith('nsec1')) {
      setError('nsec must start with "nsec1"');
      return;
    }
    setMode('nsec-submitting');
    try {
      await loginWithNsec(nsecInput.trim());
      onAuthenticated();
    } catch (err) {
      setError(errMessage(err, 'Login failed'));
      setMode('nsec-paste');
    }
  }

  async function copyGenerated() {
    if (!generated?.nsec) return;
    try {
      await navigator.clipboard.writeText(generated.nsec);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard may be blocked in insecure context */
    }
  }

  return (
    <div class="login-screen">
      <div class="login-hero">
        <h2>Hermes</h2>
        <p class="muted">Device dashboard — sign in with Nostr.</p>
      </div>

      {error && <div class="alert alert-error">{error}</div>}

      {generated ? (
        <div class="card generated-card">
          <h3>Identity created</h3>
          <p class="muted">
            Save this nsec somewhere safe. We've stored it locally so you stay
            logged in on this device, but it is your only key to this identity.
          </p>
          <div class="generated-identity">
            <div class="field">
              <label>Your npub (shareable)</label>
              <code class="text-mono generated-npub">{generated.npub}</code>
            </div>
            <div class="field">
              <label>Your nsec (secret — do not share)</label>
              <div class="generated-nsec-row">
                <code class="text-mono generated-nsec">{generated.nsec}</code>
                <button
                  type="button"
                  class="btn btn-secondary"
                  onClick={copyGenerated}
                >
                  {copied ? 'Copied' : 'Copy'}
                </button>
              </div>
            </div>
          </div>
          <button class="btn btn-block" onClick={onAuthenticated}>
            Continue to dashboard
          </button>
        </div>
      ) : (
        <>
          <button
            class="btn btn-block"
            onClick={handleGenerate}
            disabled={mode === 'generating'}
          >
            {mode === 'generating' ? 'Generating…' : 'Generate new identity'}
          </button>

          <div class="login-divider">
            <span>or</span>
          </div>

          {mode === 'nsec-paste' || mode === 'nsec-submitting' ? (
            <form class="card" onSubmit={handleNsecSubmit}>
              <div class="field">
                <label htmlFor="nsec-input">Paste your nsec</label>
                <input
                  id="nsec-input"
                  type="password"
                  value={nsecInput}
                  onInput={(e) =>
                    setNsecInput((e.target as HTMLInputElement).value)
                  }
                  placeholder="nsec1…"
                  autocomplete="off"
                  disabled={mode === 'nsec-submitting'}
                />
              </div>
              <button
                type="submit"
                class="btn btn-block"
                disabled={mode === 'nsec-submitting' || !nsecInput.trim()}
              >
                {mode === 'nsec-submitting' ? 'Logging in…' : 'Log in with nsec'}
              </button>
            </form>
          ) : (
            <button
              class="btn btn-secondary btn-block"
              onClick={() => setMode('nsec-paste')}
            >
              Log in with existing nsec
            </button>
          )}

          {hasNip07() && (
            <p class="muted login-nip07-note">
              NIP-07 extension detected — challenge/verify login is a fast-follow.
              For now, paste your nsec or generate a new identity.
            </p>
          )}
        </>
      )}
    </div>
  );
}

function errMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiError) return err.message;
  if (err instanceof Error) return err.message;
  return fallback;
}
